import cn from 'clsx';
import { cva, type VariantProps } from 'class-variance-authority';
import { useAdvancedFiltersModified } from '@pkgs/components/hooks/stores/search/useAdvancedFiltersModified';
import FiltersActive from '@pkgs/components/assets/icons/small/FiltersActive.svg';
import Filters from '@pkgs/components/assets/icons/small/Filters.svg';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { Button } from '@ev-tech/ui-core-components/Button';
import { Drawer } from '@base-ui/react/drawer';

import { useAppContext } from '../../hooks/useAppContext';

import styles from './AdvancedFilters.module.css';

const desktopButtonVariants = cva(styles.baseClass, {
  variants: {
    isAiSearchEnabled: {
      true: styles.aiEnabled,
      false: null,
    },
    isModified: {
      true: styles.modified,
      false: null,
    },
  },
});

const mobileButtonVariants = cva(styles.mobileBaseClass, {
  variants: {
    variant: {
      'filter-bar': styles.mobileFilterBar,
      'map-viewer': null,
    },
  },
});

export type AdvancedFiltersButtonVariant = NonNullable<VariantProps<typeof mobileButtonVariants>['variant']>;

export type AdvancedFiltersButtonProps = {
  className?: string;
  variant: AdvancedFiltersButtonVariant;
};

export const AdvancedFiltersButton = ({ variant, className }: AdvancedFiltersButtonProps) => {
  const { t, featureFlags } = useAppContext();
  const { isModified } = useAdvancedFiltersModified();

  return (
    <>
      {variant === 'filter-bar' && (
        <Drawer.Trigger
          render={
            <Button
              className={cn(desktopButtonVariants({ isAiSearchEnabled: featureFlags.isAiSearchEnabled, isModified }), className)}
              variant="tertiary"
              label={t('search.searchbar.filters')}
              icon={
                isModified ? (
                  <FiltersActive data-testid="search-components_filter-bar_advanced-filters-button-icon-desktop-active" />
                ) : (
                  <Filters data-testid="search-components_filter-bar_advanced-filters-button-icon-desktop" />
                )
              }
              size="small"
              testId={`search-components_filter-bar_advanced-filters-button-desktop${isModified ? '-active' : ''}`}
            />
          }
        />
      )}
      <Drawer.Trigger
        render={
          <IconButton
            as="button"
            className={cn(mobileButtonVariants({ variant }), className)}
            icon={
              isModified ? (
                <FiltersActive data-testid="search-components_filter-bar_advanced-filters-button-icon-mobile-active" />
              ) : (
                <Filters data-testid="search-components_filter-bar_advanced-filters-button-icon-mobile" />
              )
            }
            variant="primaryAlt"
            size="medium"
            label={t('search.searchbar.filters')}
            data-testid={`search-components_filter-bar_advanced-filters-button-mobile${isModified ? '-active' : ''}`}
          />
        }
      />
    </>
  );
};
